import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AlertDialog from './AlertDialog';
import { Context } from '../AuthContext';
import { useCategory } from '../CategoryContext';
import { toast } from 'react-toastify';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

const Invitation = ({ task, index, setInvitedVerticals, displayToast, acceptDisabled = false }) => {
  const { user } = useContext(Context);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { refreshVerticals } = useCategory();
  const [downloading, setDownloading] = useState(false);

  const leaveVertical = async (id) => {
    setLoading(true);
    const token = await user.getIdToken();
    const url = process.env.REACT_APP_FLASK_WEBSERVER + 'leaveVertical?vertical_id=' + id;

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 200) {
      toast.success('You have left the vertical!', {
        className: 'toast',
        autoClose: 3000,
      });
      setInvitedVerticals((prev) => prev.map((v) => (v.id === id ? { ...v, status: 'Left' } : v)));
      refreshVerticals();
    }
    setLoading(false);
  };

  const acceptInvitation = async (vertical) => {
    setLoading(true);
    const token = await user.getIdToken();
    const url =
      process.env.REACT_APP_FLASK_WEBSERVER + 'acceptInvitation?vertical_id=' + vertical.id;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 200) {
      toast.success(
        <>
          <span>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingBottom: '10px' }}
            >
              <CheckCircleIcon style={{ color: '#09b899' }} />
              <span
                style={{ color: 'black' }}
              >{`Invitation accepted! Explore the vertical: \n`}</span>
            </div>

            <a
              href={`/vertical/${vertical.id}/home`}
              target="_blank"
              style={{
                color: '#356AC3',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '20px',
              }}
            >
              <span>
                {vertical.name > 22 ? vertical.name.substring(0, 22) + '...' : vertical.name}
              </span>
              <OpenInNewIcon />
            </a>
          </span>
        </>,
        {
          className: 'toast',
          autoClose: 8000,
          icon: false,
        }
      );
      setInvitedVerticals((prev) =>
        prev.map((v) => (v.id === vertical.id ? { ...v, status: 'Joined' } : v))
      );
      refreshVerticals();
    } else {
      toast.error('Unable to accept invitation', {
        className: 'toast',
        autoClose: 8000,
      });
    }
    setLoading(false);
  };

  const declineInvitation = async (vertical) => {
    setLoading(true);
    const token = await user.getIdToken();
    const url =
      process.env.REACT_APP_FLASK_WEBSERVER + 'declineInvitation?vertical_id=' + vertical.id;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 200) {
      toast.success('Invitation declined!', {
        className: 'toast',
        autoClose: 3000,
      });
      setInvitedVerticals((prev) =>
        prev.map((v) => (v.id === vertical.id ? { ...v, status: 'Declined' } : v))
      );
      refreshVerticals();
    } else {
      toast.error('Unable to decline invitation', {
        className: 'toast',
        autoClose: 3000,
      });
    }
    setLoading(false);
  };

  const downloadVertical = async (vertical_id) => {
    setDownloading(true);
    displayToast();
    const token = await user.getIdToken();
    const url = `${process.env.REACT_APP_FLASK_WEBSERVER}download_vertical?vertical_id=${vertical_id}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${task.name}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloading(false);
  };

  const statusColor = { color: '#1976d2' };

  const iconBtnStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <>
      <tr
        onClick={() => task.status === 'Joined' && navigate(`/vertical/${task.id}/home`)}
        style={{ cursor: task.status === 'Joined' ? 'pointer' : 'default' }}
      >
        <td className="result-item" style={{ paddingLeft: '10px' }}>
          {task.name}
        </td>

        <td className="result-item">{task.subscribed_at || '-'}</td>
        <td className="result-item">{task.creator_name}</td>

        <td className="result-item">
          {task.permission === 'Editor'
            ? 'Basic Editor'
            : task.permission === 'AdvancedEditor'
            ? 'Advanced Editor'
            : task.permission}
        </td>
        <td className="result-item" style={statusColor}>
          {task.status}
        </td>
        <td className="result-item">
          {task.status === 'Invited' && (
            <div style={{ display: 'flex', gap: '10px' }}>
              {!loading ? (
                <>
                  <button
                    style={iconBtnStyle}
                    onClick={(e) => {
                      e.stopPropagation();
                      declineInvitation(task, index);
                    }}
                  >
                    <img src={`${process.env.PUBLIC_URL}/decline.svg`} alt="decline" />
                  </button>
                  <button
                    style={{
                      ...iconBtnStyle,
                      cursor: acceptDisabled ? 'not-allowed' : 'pointer',
                      opacity: acceptDisabled ? 0.4 : 1,
                    }}
                    disabled={acceptDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (acceptDisabled) return;
                      acceptInvitation(task, index);
                    }}
                  >
                    <img src={`${process.env.PUBLIC_URL}/accept.svg`} alt="accept" />
                  </button>
                </>
              ) : (
                <FontAwesomeIcon icon={faSpinner} spin size="sm" />
              )}
            </div>
          )}

          {task.status === 'Joined' && (
            <div style={{ display: 'flex', gap: '10px' }}>
              {!loading ? (
                <>
                  <AlertDialog
                    id={task.id}
                    action={(e) => leaveVertical(e)}
                    index={index}
                    dialogTitle="Leave Vertical"
                    dialogContentText="This action cannot be undone. This will permanently remove you from the vertical."
                  />

                  {task.permission === 'Editor' && (
                    <>
                      {downloading ? (
                        <FontAwesomeIcon icon={faSpinner} spin size="lg" />
                      ) : (
                        <DownloadOutlinedIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadVertical(task.id);
                          }}
                        />
                      )}
                    </>
                  )}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/vertical/${task.id}/home`);
                    }}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <img src={`${process.env.PUBLIC_URL}/open.svg`} alt="open" />
                  </div>
                </>
              ) : (
                <FontAwesomeIcon icon={faSpinner} spin size="sm" />
              )}
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

export default Invitation;
